"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, Loader, Phone, Clock, MessageCircle, Mail, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[hsl(150_45%_34%/0.08)] border border-[hsl(150_45%_34%/0.3)] rounded-lg">
        <div className="flex items-start gap-3">
          <div className="mt-1 size-2 rounded-lg bg-[hsl(150_45%_34%)] shrink-0" />
          <div>
            <p className="font-mono text-2xl uppercase tracking-tight text-foreground mb-2">
              {"Solicitud enviada"}
            </p>
            <p className="text-foreground/70 mb-6">{successMessage}</p>
            <Button onClick={resetForm} variant="outline">
              {"Enviar otra"}
            </Button>
          </div>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Nombre requerido",
      minLength: {
        value: 2,
        message: "Mín 2 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
              {"Nombre completo"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Juan Pérez" className="h-12 rounded-lg bg-background border-[color:var(--border)] px-4 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Teléfono requerido"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                {"Teléfono"}
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="600 000 000" className="h-12 rounded-lg bg-background border-[color:var(--border)] px-4 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="city" rules={{
        required: "Ciudad requerida"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                {"Ciudad / Localidad"}
              </FormLabel>
              <FormControl>
                <select {...field} className="h-12 w-full rounded-lg bg-background border border-[color:var(--border)] px-4 text-foreground text-sm focus-visible:border-primary focus-visible:ring-primary/40 outline-none">
                  <option value="" disabled>{"Selecciona tu ciudad"}</option>
                  <option value="Totana">{"Totana"}</option>
                  <option value="Murcia">{"Murcia"}</option>
                  <option value="Alicante">{"Alicante"}</option>
                  <option value="Almería">{"Almería"}</option>
                  <option value="Otra">{"Otra"}</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Por favor describe tu consulta"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
              {"¿Qué necesitas?"}
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Describa su consulta, avería o proyecto..." className="min-h-32 rounded-lg bg-background border-[color:var(--border)] px-4 py-3 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-[hsl(0_62%_44%/0.08)] border border-[hsl(0_62%_44%/0.3)] rounded-lg flex items-start gap-3">
          <AlertCircle className="size-4 text-[hsl(0_62%_44%)] shrink-0 mt-0.5" />
          <p className="text-sm text-[hsl(0_62%_44%)]">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.1em] text-sm font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {"Enviando..."}
            </> : "Enviar solicitud"}
        </Button>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          {"Tus datos están seguros. No te enviaremos spam."}
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const reduce = useReducedMotion();
  return <section data-section-id="32898" id="contacto-directo" className="bg-background py-20 md:py-32 relative overflow-hidden">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div className="flex flex-col justify-between space-y-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="block w-[2px] h-3 bg-primary" />
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  {"Contacto directo"}
                </span>
                <span className="block w-[2px] h-3 bg-primary" />
              </div>

              <h2 className="font-mono font-bold uppercase text-foreground leading-[0.95] tracking-tight text-[clamp(2.2rem,4.5vw,3.8rem)] mb-6">
                {"Habla con nosotros directamente"}
              </h2>

              <div className="mb-8 space-y-2">
                <Button asChild className="w-full h-14 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold text-base rounded-lg shadow-sm transition-colors uppercase tracking-[0.05em] flex items-center justify-center gap-2">
                  <Link to="https://wa.me/34657068444" newTab>
                    <MessageCircle className="size-5 fill-current" />
                    <span>{"Escríbenos por WhatsApp"}</span>
                  </Link>
                </Button>
                <div className="text-center">
                  <Link to="tel:+34657068444" className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors">
                    {"+34 657 068 444"}
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3.5 p-4 rounded-lg bg-[hsl(34_22%_92%)] border border-[color:var(--border)]">
                  <Phone className="size-5 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-0.5">
                      {"Llámanos"}
                    </p>
                    <Link to="tel:+34657068444" className="font-mono text-xl font-bold text-foreground hover:text-primary transition-colors">
                      {"+34 657 068 444"}
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-lg bg-[hsl(34_22%_92%)] border border-[color:var(--border)]">
                  <Mail className="size-5 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-0.5">
                      {"Email de contacto"}
                    </p>
                    <Link to="mailto:info@serviciosdereparaciones.com" className="font-sans text-base font-semibold text-foreground hover:text-primary transition-colors break-all">
                      {"info@serviciosdereparaciones.com"}
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-lg bg-[hsl(34_22%_92%)] border border-[color:var(--border)]">
                  <MapPin className="size-5 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-0.5">
                      {"Ubicación"}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {"Oficina central: La Vía Nueva 69, Totana, Murcia"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-lg bg-[hsl(34_22%_92%)] border border-[color:var(--border)]">
                  <Clock className="size-5 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-0.5">
                      {"Horario de atención"}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {"Lunes a Viernes de 8:00 a 18:00 | Urgencias 24 horas"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-start gap-3">
              <span className="inline-block size-2 rounded-lg bg-primary shrink-0 mt-2" />
              <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                {"Si tienes una emergencia, la vía más rápida es llamarnos o escribirnos por WhatsApp."}
              </p>
            </div>
          </div>

          {}
          <motion.div initial={reduce ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.62,
          ease: [0.22, 1, 0.36, 1]
        }}>
            <div className="bg-[hsl(34_22%_92%)] rounded-lg border border-[color:var(--border)] p-6 sm:p-8 lg:p-10">
              <h2 className="font-mono font-bold uppercase text-foreground leading-[0.95] tracking-tight text-[clamp(1.8rem,3.5vw,2.5rem)] mb-2">
                {"Solicita tu presupuesto gratis"}
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                {"Sin compromiso. Respuesta garantizada en menos de 24 horas."}
              </p>

              <FormProvider formId="contact_form_contact" formKey="contact_form_contact" sectionName="contacto-directo" defaultValues={{
              name: "",
              phone: "",
              city: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              phone: {
                required: true
              },
              city: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Enviar solicitud" successMessage="Gracias. Un técnico de Servicios de reparaciones contactará contigo en menos de 24 horas." errorMessage="Error al enviar. Inténtelo de nuevo o llámenos.">
                <InquiryForm />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}